import { reportTypes } from '../utils/reports';
import { CREATIVE_GALLERY_REPORT_TYPE } from '../creativeGallery/consts';
import { BASE_BOOKMARK_GROUPS, BOOKMARK_EVENT, BOOKMARK_TYPE_TO_GROUP, CREATIVE_IQ_BOOKMARK_GROUPS } from './consts';

export const dispatchBookmarkEvent = (type, data) => {
    window.dispatchEvent(new CustomEvent(BOOKMARK_EVENT, { detail: { type, data } }));
};

const CREATIVE_GALLERY_PATH = '/react/creative-gallery';

const BOOKMARK_ROUTES = {
    report: '/reports',
    [reportTypes.pivot]: '/react/pivot',
    [reportTypes.retention]: '/react/retention',
    [reportTypes.fraudProtected]: '/react/fraud/protected',
    [reportTypes.fraudSuspicious]: '/react/fraud/suspicious',
    [reportTypes.fraudRejected]: '/react/fraud/rejected',
    [reportTypes.adMonetization]: '/react/admonetization',
    [reportTypes.crossDevice]: '/react/cross-device',
    [reportTypes.skan]: '/react/skadnetwork_report',
    [reportTypes.skanSummary]: '/react/skadnetwork_summary_report',
    [reportTypes.unifiedReport]: '/react/unified_report',
    [reportTypes.creativeExplore]: '/react/creative-explore',
    [CREATIVE_GALLERY_REPORT_TYPE]: CREATIVE_GALLERY_PATH,
};

export const getBookmarkReportStateKey = type => (type === 'report' ? reportTypes.mainReport : type);

export const getBookmarkGroupsToShow = (featureFlags = []) => [
    ...BASE_BOOKMARK_GROUPS,
    ...(featureFlags.includes('creative_iq') ? CREATIVE_IQ_BOOKMARK_GROUPS : []),
];

const matchesSearch = (bookmark, searchText) =>
    !searchText || (bookmark.name || '').toLowerCase().includes(searchText.toLowerCase());

export const filterTopBookmarks = (bookmarks, topBookmarkIds, searchText) =>
    (bookmarks || []).filter(
        bookmark => (topBookmarkIds || []).includes(bookmark.uuid) && matchesSearch(bookmark, searchText)
    );

export const filterBookmarks = (bookmarks, { isPrivate, group, searchText, userEmail }) => {
    const types = Object.keys(BOOKMARK_TYPE_TO_GROUP).filter(type => BOOKMARK_TYPE_TO_GROUP[type] === group);
    return (bookmarks || []).filter(bookmark => {
        if (isPrivate && bookmark.creator !== userEmail) return false;
        if (!isPrivate && !bookmark.is_shared) return false;
        if (types.length > 0 && !types.includes(bookmark.type)) return false;
        return matchesSearch(bookmark, searchText);
    });
};

export const canUserDeleteBookmark = (bookmark, userEmail) => bookmark.creator === userEmail || !!bookmark.is_shared;

// The API forks foreign bookmarks instead of updating them in place.
export const canUserEditBookmark = (bookmark, userEmail) => bookmark.creator === userEmail;

// Decode one level so the backend's escape(name) does not compound on re-save.
// Ampersands must be decoded last to preserve nested entities.
const HTML_ENTITY_DECODE_ORDER = [
    ['&lt;', '<'],
    ['&gt;', '>'],
    ['&quot;', '"'],
    ['&#x27;', "'"],
    ['&#39;', "'"],
    ['&amp;', '&'],
];

export const decodeBookmarkName = (name = '') =>
    HTML_ENTITY_DECODE_ORDER.reduce((value, [entity, char]) => value.split(entity).join(char), name);

export const buildBookmarkSavePayload = bookmark => {
    const { bookmark_id: ignoredId, bookmark_creator: ignoredCreator, api_version: ignoredVersion, ...query } =
        bookmark.url || {};
    return {
        name: decodeBookmarkName(bookmark.name),
        query,
        type: bookmark.type,
        is_shared: bookmark.is_shared,
        time_period_type: bookmark.time_period_type,
        time_period: bookmark.time_period,
        relative_days_back: bookmark.relative_days_back,
        absolute_start_date: bookmark.absolute_start_date,
        absolute_end_date: bookmark.absolute_end_date,
        report_type: bookmark.report_type,
        compare_absolute_start_date: bookmark.compare_absolute_start_date,
        compare_absolute_end_date: bookmark.compare_absolute_end_date,
        relative_comparison_period: bookmark.relative_comparison_period,
        comparison_days_gap: bookmark.comparison_days_gap,
    };
};

export const getActiveBookmarkId = (hash = window.location.hash) => {
    const queryStart = hash.indexOf('?');
    if (queryStart < 0) return null;
    const params = new URLSearchParams(hash.slice(queryStart + 1));
    try {
        const query = params.get('query');
        if (query) return JSON.parse(query).bookmark_id || null;
        const galleryId = params.get('bookmark_id');
        return galleryId ? JSON.parse(galleryId) : null;
    } catch (error) {
        return null;
    }
};

export const buildBookmarkOpenAction = (bookmark, location) => {
    const bookmarkQuery = {
        ...(bookmark.url || {}),
        bookmark_id: bookmark.uuid,
        bookmark_creator: bookmark.creator,
        api_version: 1,
    };

    const urlParams = {};
    if (bookmark.type === CREATIVE_GALLERY_REPORT_TYPE) {
        Object.entries(bookmarkQuery).forEach(([key, value]) => {
            urlParams[key] = JSON.stringify(value);
        });
    } else {
        urlParams.query = JSON.stringify(bookmarkQuery);
    }

    const path = BOOKMARK_ROUTES[bookmark.type];
    if (!path) {
        return null;
    }
    const hash = `${path}?${new URLSearchParams(urlParams).toString()}`;

    if (bookmark.impersonated_org && bookmark.impersonated_org_data && bookmark.impersonated_org_data.customer_id) {
        const currentCustomerId = /customer_id=([^&]+)/.exec(location.search);
        if (currentCustomerId) {
            const useSearch = location.search.replace(currentCustomerId[1], bookmark.impersonated_org_data.customer_id);
            const { protocol, host } = window.location;
            return { href: `${protocol}//${host}/${useSearch}#${hash}`, bookmarkQuery };
        }
    }

    if (bookmark.type === CREATIVE_GALLERY_REPORT_TYPE && location.href.includes(CREATIVE_GALLERY_PATH)) {
        return { hash, reload: true, bookmarkQuery };
    }

    return { hash, bookmarkQuery };
};
